/**
 * client.ts  -  Axios-based API client with:
 *  - Automatic Authorization header injection
 *  - Silent token refresh on 401 (single retry, queued to avoid parallel refreshes)
 *  - Logout on refresh failure
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import { useAuthStore } from "@/store/authStore";
import { reportError } from "@/utils/errorReporter";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

// ─── Core instance ────────────────────────────────────────────────────────────

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // include HttpOnly refresh cookie
  headers: { "Content-Type": "application/json" },
});

// ─── Request interceptor: inject access token ─────────────────────────────────
// If an impersonation token exists in sessionStorage, use it instead of the
// normal access token. Impersonation tokens are self-contained short-lived JWTs.

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const impersonationToken = sessionStorage.getItem("impersonation_token");
  const token = impersonationToken ?? useAuthStore.getState().accessToken;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Response interceptor: silent refresh on 401 ─────────────────────────────

let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  config: AxiosRequestConfig;
}> = [];

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    // ── Impersonation preview: ALL errors resolve as empty object ────────────
    // Generic role preview tokens have no real DB user, so controllers error.
    // Catch every error (including 401) so we never trigger the refresh loop.
    // Return {} not null  -  hooks do .then(d => d.classes) which would throw
    // TypeError on null, but safely returns undefined on {}, giving empty state.
    const isImpersonating = !!sessionStorage.getItem("impersonation_token");
    if (isImpersonating) {
      return Promise.resolve({
        data: {} as Record<string, never>,
        status: 200,
        statusText: "Preview Mode",
        headers: error.response?.headers ?? {},
        config: error.config,
        request: error.request,
      });
    }

    if (error.response?.status !== 401 || originalRequest._retry) {
      // Show the server's plain-English reason, not "Request failed with status code 422".
      const message = (error.response?.data?.error as { message?: string } | undefined)?.message;
      if (message) error.message = message;
      const status = error.response?.status as number | undefined;
      if (status !== 401 && !String(error.config?.url ?? "").includes("client-errors")) {
        reportError({
          kind: "api",
          message: error.message,
          status,
          api: `${(error.config?.method ?? "").toUpperCase()} ${String(error.config?.url ?? "").split("?")[0]}`,
        });
      }
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      // Queue requests while refresh is in flight
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    isRefreshing = true;

    try {
      const { data } = await axios.post<{ accessToken: string }>(
        `${BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true },
      );

      const newToken = data.accessToken;
      useAuthStore.getState().setAuth(newToken, useAuthStore.getState().user!);

      // Drain the queue
      pendingQueue.forEach(({ resolve, config }) => {
        if (config.headers) {
          (config.headers as Record<string, string>).Authorization =
            `Bearer ${newToken}`;
        }
        resolve(apiClient(config));
      });
      pendingQueue = [];

      // Retry original
      if (originalRequest.headers) {
        (originalRequest.headers as Record<string, string>).Authorization =
          `Bearer ${newToken}`;
      }
      return apiClient(originalRequest);
    } catch (refreshError) {
      pendingQueue.forEach(({ reject }) => reject(refreshError));
      pendingQueue = [];
      // Clear auth and let React Router re-render to the right place
      // (LandingPage at "/", or a clean /login redirect on protected routes).
      // No hard window.location redirect  -  that caused a flash/bounce loop
      // for visitors with an expired stored session.
      useAuthStore.getState().clearAuth();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

// ─── Typed helpers ────────────────────────────────────────────────────────────

export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config).then((r) => r.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config).then((r) => r.data),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config).then((r) => r.data),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config).then((r) => r.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then((r) => r.data),
};
